import React, { useCallback, useEffect, useState } from 'react';
import { Search, Check, X, Ban, ChevronLeft, ChevronRight, Loader2, Eye } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { usePermissions } from '../../admin/usePermissions';
import { toast } from 'sonner';

const STATUSES = ['all', 'submitted', 'live', 'draft', 'completed', 'cancelled', 'rejected'];
const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-amber-50 text-amber-700',
  live: 'bg-emerald-50 text-emerald-700',
  completed: 'bg-blue-50 text-blue-700',
  cancelled: 'bg-red-50 text-red-600',
  rejected: 'bg-red-50 text-red-600',
};

export default function AdminEventsPage() {
  const { has } = usePermissions();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approveConfirm, setApproveConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { limit, offset };
      if (q) params.q = q;
      if (status !== 'all') params.status = status;
      const r = await adminApi.get('/events', { params });
      setItems(r.data?.items || r.data || []);
      setTotal(r.data?.total ?? (r.data?.length || 0));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, [q, status, offset]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const approveEv = async (id) => {
    setBusy(true);
    try { await adminApi.post(`/events/${id}/approve`); toast.success('Event approved → live'); setDetail(null); setApproveConfirm(null); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setBusy(false); }
  };

  const rejectEv = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    setBusy(true);
    try { await adminApi.post(`/events/${id}/reject`, { reason }); toast.success('Event rejected'); setDetail(null); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setBusy(false); }
  };

  const forceCancel = async (id) => {
    const reason = window.prompt('Cancellation reason:');
    if (!reason) return;
    setBusy(true);
    try { await adminApi.post(`/events/${id}/force-cancel`, { reason }); toast.success('Event force-cancelled'); setDetail(null); await load(); }
    catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setBusy(false); }
  };

  const pages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  return (
    <div data-testid="admin-events-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Events</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input data-testid="events-search" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder="Search events..." className="py-2 text-sm bg-transparent focus:outline-none flex-1" />
        </div>
        <select data-testid="events-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700">
          {STATUSES.map((s) => <option key={s} value={s}>{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="events-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Event</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Organizer</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">City</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Enq</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((ev) => {
                  const st = ev.status || 'live';
                  return (
                    <tr key={ev.id} data-testid={`event-row-${ev.id}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <img src={ev.banner_url} alt="" className="w-9 h-9 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                          <span className="font-semibold text-gray-900 truncate max-w-[180px]">{ev.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{ev.organizer_name}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{ev.category}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{ev.city}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{dayjs(ev.start_datetime).format('MMM D, YY')}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[st] || 'bg-gray-100 text-gray-600'}`}>{st.toUpperCase()}</span>
                      </td>
                      <td className="px-4 py-3 text-center text-xs font-semibold text-gray-700">{ev.enquiry_count}</td>
                      <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                        <button data-testid={`event-detail-${ev.id}`} onClick={() => setDetail(ev)} className="p-1.5 rounded hover:bg-gray-100" title="Details"><Eye className="w-4 h-4 text-gray-500" /></button>
                        {st === 'submitted' && has('events.approve') && (
                          <>
                            <button data-testid={`event-approve-${ev.id}`} onClick={() => setApproveConfirm({ id: ev.id, title: ev.title })} className="p-1.5 rounded hover:bg-emerald-50"><Check className="w-4 h-4 text-emerald-600" /></button>
                            <button data-testid={`event-reject-${ev.id}`} onClick={() => rejectEv(ev.id)} className="p-1.5 rounded hover:bg-red-50"><X className="w-4 h-4 text-red-500" /></button>
                          </>
                        )}
                        {(st === 'live' || st === 'submitted') && has('events.cancel') && (
                          <button data-testid={`event-cancel-${ev.id}`} onClick={() => forceCancel(ev.id)} className="p-1.5 rounded hover:bg-red-50"><Ban className="w-4 h-4 text-red-500" /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {items.length === 0 && <p className="text-center text-sm text-gray-400 py-10">No events found</p>}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">{total} total · Page {page}/{pages}</p>
          <div className="flex gap-1">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
            <button disabled={page >= pages} onClick={() => setOffset(offset + limit)} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetail(null)} />
          <div className="relative bg-white h-full w-full max-w-lg overflow-y-auto shadow-xl" data-testid="event-detail-drawer">
            <div className="relative">
              <img src={detail.banner_url} alt="" className="w-full h-48 object-cover bg-gray-100" />
              <button onClick={() => setDetail(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5">
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[detail.status] || 'bg-gray-100 text-gray-600'}`}>{(detail.status || 'live').toUpperCase()}</span>
              <h2 className="text-lg font-black text-gray-900 mt-2">{detail.title}</h2>
              <p className="text-xs text-gray-500">{detail.organizer_name} · {detail.category} · {detail.city}</p>
              <div className="mt-3 space-y-2 text-sm">
                <p><span className="font-bold text-gray-700">When:</span> {dayjs(detail.start_datetime).format('MMM D, YYYY h:mm A')} — {dayjs(detail.end_datetime).format('h:mm A')}</p>
                <p><span className="font-bold text-gray-700">Venue:</span> {detail.venue_name}, {detail.venue_address}</p>
                <p><span className="font-bold text-gray-700">Views:</span> {detail.view_count} · <span className="font-bold text-gray-700">Enquiries:</span> {detail.enquiry_count}</p>
                <p className="text-gray-600">{detail.description}</p>
              </div>
              <div className="flex flex-col gap-2 mt-5">
                {detail.status === 'submitted' && has('events.approve') && (
                  <>
                    <button data-testid="drawer-approve-event" disabled={busy} onClick={() => setApproveConfirm({ id: detail.id, title: detail.title })} className="py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">Approve</button>
                    <button data-testid="drawer-reject-event" disabled={busy} onClick={() => rejectEv(detail.id)} className="py-2.5 rounded-lg bg-red-50 text-red-600 text-sm font-bold hover:bg-red-100 disabled:opacity-50">Reject</button>
                  </>
                )}
                {(detail.status === 'live' || detail.status === 'submitted') && (
                  <button data-testid="drawer-cancel-event" disabled={busy} onClick={() => forceCancel(detail.id)} className="py-2.5 rounded-lg bg-gray-100 text-gray-700 text-sm font-bold hover:bg-gray-200 disabled:opacity-50">Force Cancel</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Approve Confirmation Dialog */}
      {approveConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setApproveConfirm(null)} />
          <div className="relative bg-white rounded-xl w-full max-w-sm p-6 mx-4 shadow-xl" data-testid="event-approve-confirm-dialog">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center"><Check className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Approve Event</h3>
                <p className="text-xs text-gray-500 mt-0.5">Approve and publish &lsquo;{approveConfirm.title}&rsquo;? This will make the event Live and visible to all users.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="event-approve-cancel-btn" onClick={() => setApproveConfirm(null)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="event-approve-confirm-btn" disabled={busy} onClick={() => approveEv(approveConfirm.id)}
                className="flex-1 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
